import React from "react";
import { styled, keyframes } from "styled-components";
import { useTranslation } from "react-i18next";
import colorSet from "@/src/styles/color";
import Icon from "@/src/components/atom/Icon";
import CheckmarkCircleGreenFillSvg from "@/src/assets/icons/icon-checkmark-circle-green-fill.svg";
import IconErrorCircleFillSvg from "@/src/assets/icons/icon-error-circle-fill.svg";
import IconInfoRedSvg from "@/src/assets/icons/icon-alert-prefix-red.svg";
import CloseSvg from "@/src/assets/icons/icon-cancle-black.svg";

interface HistoryItem {
  id: number;
  status: "completed" | "rejected";
  title: string;
  user: string;
  timestamp: string;
  rejectionReason?: string;
}

interface ContractHistoryCardProps {
  historyItems: HistoryItem[];
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  modalTitle?: string;
}

const ContractHistoryCard: React.FC<ContractHistoryCardProps> = ({
  historyItems,
  open,
  onOpenChange,
  modalTitle = "계약 히스토리",
}) => {
  const { t } = useTranslation();
  const getStatusIcon = (status: HistoryItem["status"]) => {
    switch (status) {
      case "completed":
        return <Icon iconSrc={CheckmarkCircleGreenFillSvg} iconSize={16} />;
      case "rejected":
        return <Icon iconSrc={IconErrorCircleFillSvg} iconSize={16} />;
      default:
        return <Icon iconSrc={CheckmarkCircleGreenFillSvg} iconSize={16} />;
    }
  };

  const getStatusColor = (status: HistoryItem["status"]) => {
    switch (status) {
      case "completed":
        return "#222729";
      case "rejected":
        return "#222729";
      default:
        return "#222729";
    }
  };

  // If open and onOpenChange are provided, render as modal
  if (open !== undefined && onOpenChange) {
    return (
      <>
        <ModalOverlay onClick={() => onOpenChange(false)} />
        <ModalContainer>
          {/* Header */}
          <ModalHeader>
            <ModalTitle>{modalTitle}</ModalTitle>
            <CloseButton onClick={() => onOpenChange(false)}>
              <Icon iconSrc={CloseSvg} iconSize={20} />
            </CloseButton>
          </ModalHeader>

          {/* Content */}
          <ModalBody>
            <ModalHistoryList>
              {historyItems.map((item, index) => (
                <ModalHistoryItem key={item.id}>
                  {/* Timeline */}
                  <Timeline>
                    <TimelineTop />
                    <TimelineIcon>{getStatusIcon(item.status)}</TimelineIcon>
                    {index < historyItems.length - 1 && <TimelineBottom />}
                  </Timeline>

                  {/* Content */}
                  <ModalItemContent>
                    <ModalContentHeader>
                      <ModalStatusTitle>{item.title}</ModalStatusTitle>
                      <ModalTimestamp>{item.timestamp}</ModalTimestamp>
                    </ModalContentHeader>
                    <ModalUserName>{item.user}</ModalUserName>

                    {/* Rejection Reason */}
                    {item.rejectionReason && (
                      <ModalRejectionCard>
                        <ModalRejectionHeader>
                          <Icon iconSrc={IconInfoRedSvg} iconSize={16} />
                          <ModalRejectionTitle>
                            {t("contract:reject.reasonLabel")}
                          </ModalRejectionTitle>
                        </ModalRejectionHeader>
                        <ModalRejectionText>
                          {item.rejectionReason}
                        </ModalRejectionText>
                      </ModalRejectionCard>
                    )}
                  </ModalItemContent>
                </ModalHistoryItem>
              ))}
            </ModalHistoryList>
          </ModalBody>
        </ModalContainer>
      </>
    );
  }

  // Original card view
  return (
    <Container>
      {/* Header */}
      <Header>
        <Title>{modalTitle}</Title>
      </Header>

      {/* History List */}
      <ScrollContainer>
        <HistoryList>
          {historyItems.map((item, index) => (
            <HistoryItemWrapper key={item.id}>
              {/* Status Icon */}
              <IconWrapper>{getStatusIcon(item.status)}</IconWrapper>

              {/* Content */}
              <Content>
                <ContentHeader>
                  <StatusTitle color={getStatusColor(item.status)}>
                    {item.title}
                  </StatusTitle>
                  <Timestamp>{item.timestamp}</Timestamp>
                </ContentHeader>
                <UserName>{item.user}</UserName>

                {/* Rejection Reason */}
                {item.rejectionReason && (
                  <RejectionCard>
                    <RejectionHeader>
                      <Icon iconSrc={IconInfoRedSvg} iconSize={16} />
                      <RejectionTitle>
                        {t("contract:reject.reasonLabel")}
                      </RejectionTitle>
                    </RejectionHeader>
                    <RejectionText>{item.rejectionReason}</RejectionText>
                  </RejectionCard>
                )}
              </Content>

              {/* Timeline line */}
              {index < historyItems.length - 1 && <TimelineLine />}
            </HistoryItemWrapper>
          ))}
        </HistoryList>
      </ScrollContainer>
    </Container>
  );
};

export default ContractHistoryCard;

const Container = styled.div`
  background: ${colorSet.white};
  border-radius: 16px;
  width: 100%;
  max-width: 496px;
  overflow: hidden;
  box-shadow: 0px 4px 24px 0px rgba(0, 0, 0, 0.1);
`;

const ModalContent = styled.div`
  padding: 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px;
  border-bottom: 1px solid ${colorSet.gray2};
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${colorSet.gray9};
  margin: 0;
`;

const ScrollContainer = styled.div`
  overflow-y: auto;
  max-height: calc(80vh - 80px);
`;

const HistoryList = styled.div`
  padding: 24px;
`;

const HistoryItemWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  position: relative;

  &:not(:last-child) {
    margin-bottom: 16px;
  }
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  margin-top: 2px;
`;

const Content = styled.div`
  flex: 1;
  min-width: 0;
`;

const ContentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StatusTitle = styled.h3<{ color: string }>`
  font-size: 14px;
  font-weight: 500;
  color: ${({ color }) => color};
  margin: 0;
`;

const Timestamp = styled.span`
  font-size: 12px;
  color: ${colorSet.gray6};
  margin-left: 8px;
  flex-shrink: 0;
`;

const UserName = styled.p`
  font-size: 14px;
  color: ${colorSet.gray7};
  margin: 4px 0 0 0;
`;

const RejectionCard = styled.div`
  margin-top: 12px;
  padding: 12px;
  background-color: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 8px;
`;

const RejectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const RejectionTitle = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #dc2626;
  margin: 0 0 4px 0;
`;

const RejectionText = styled.p`
  font-size: 14px;
  color: #b91c1c;
  line-height: 1.5;
  margin: 0;
  word-break: break-word;
`;

const TimelineLine = styled.div`
  position: absolute;
  left: 10px;
  top: 32px;
  width: 1px;
  height: 24px;
  background-color: ${colorSet.gray3};
`;

// Animations
const overlayKeyframe = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const contentShow = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, -48%) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, -50%) scale(1);
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 999;
  animation: ${overlayKeyframe} 150ms cubic-bezier(0.16, 1, 0.3, 1);
`;

const ModalContainer = styled.div`
  width: 496px;
  max-width: calc(100vw - 48px);
  height: 800px;
  max-height: calc(100vh - 100px);
  min-height: 400px;
  background: white;
  box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1000;
  animation: ${contentShow} 150ms cubic-bezier(0.16, 1, 0.3, 1);

  @media (max-height: 900px) {
    height: calc(100vh - 100px);
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 16px 16px 24px;
  border-bottom: 1px solid #f1f4f6;
`;

const ModalTitle = styled.h1`
  flex: 1;
  color: black;
  font-size: 20px;
  font-family: "Pretendard";
  font-weight: 700;
  line-height: 30px;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  padding: 6px;
  background: white;
  box-shadow: 0px 1px 2px rgba(5, 29, 57, 0.1);
  border-radius: 8px;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f9fafb;
  }
`;

const ModalBody = styled.div`
  flex: 1;
  padding: 20px;
  background: white;
  overflow-y: auto;
`;

const ModalHistoryList = styled.div`
  display: flex;
  flex-direction: column;
`;

const ModalHistoryItem = styled.div`
  display: flex;
  gap: 8px;
`;

const Timeline = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 20px;
`;

const TimelineTop = styled.div`
  width: 2px;
  height: 12px;
  background: white;
`;

const TimelineIcon = styled.div`
  width: 16px;
  height: 16px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TimelineBottom = styled.div`
  width: 2px;
  flex: 1;
  background: #f1f4f6;
`;

const ModalItemContent = styled.div`
  flex: 1;
  padding-top: 8px;
  padding-bottom: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ModalContentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ModalStatusTitle = styled.div`
  flex: 1;
  color: #222729;
  font-size: 16px;
  font-family: "Pretendard";
  font-weight: 700;
  line-height: 24px;
`;

const ModalTimestamp = styled.div`
  color: #6e777c;
  font-size: 13px;
  font-family: "Pretendard";
  font-weight: 400;
  line-height: 18px;
`;

const ModalUserName = styled.div`
  color: #5e696e;
  font-size: 14px;
  font-family: "Pretendard";
  font-weight: 500;
  line-height: 18px;
`;

const ModalRejectionCard = styled.div`
  margin-top: 8px;
  padding: 12px 16px;
  background: #ffefef;
  border-radius: 8px;
  border: 1px solid #fad8d8;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ModalRejectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ModalRejectionTitle = styled.div`
  flex: 1;
  color: #d64c4c;
  font-size: 14px;
  font-family: "Pretendard";
  font-weight: 500;
  line-height: 18px;
`;

const ModalRejectionText = styled.div`
  color: #5e696e;
  font-size: 14px;
  font-family: "Pretendard";
  font-weight: 400;
  line-height: 18px;
  word-break: break-word;
`;
